import { useEffect, useState } from "react";
import type { ChoiceOption } from "../grid/applyEdit";

interface Props {
  initialChoices: ChoiceOption[];
  /** 同ファイル内のラベル一覧(target 補完用) */
  labelSuggestions?: string[];
  onSave: (choices: ChoiceOption[]) => void;
  onCancel: () => void;
}

export function ChoiceEditor({ initialChoices, labelSuggestions, onSave, onCancel }: Props) {
  const [choices, setChoices] = useState<ChoiceOption[]>(
    initialChoices.length > 0
      ? initialChoices.map((c) => ({ ...c }))
      : [{ text: "", target: "#" }],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const updateChoice = (i: number, patch: Partial<ChoiceOption>) => {
    setChoices((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  };

  const removeChoice = (i: number) => {
    setChoices((prev) => prev.filter((_, idx) => idx !== i));
  };

  const addChoice = () => {
    setChoices((prev) => [...prev, { text: "", target: "#" }]);
  };

  const moveUp = (i: number) => {
    if (i === 0) return;
    setChoices((prev) => {
      const next = [...prev];
      [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
      return next;
    });
  };

  const moveDown = (i: number) => {
    setChoices((prev) => {
      if (i >= prev.length - 1) return prev;
      const next = [...prev];
      [next[i], next[i + 1]] = [next[i + 1]!, next[i]!];
      return next;
    });
  };

  const save = () => {
    const valid = choices.filter((c) => c.text.trim() && c.target.trim());
    if (valid.length === 0) {
      alert("少なくとも 1 つの有効な選択肢が必要です");
      return;
    }
    onSave(valid);
  };

  return (
    <div className="modal-backdrop" onMouseDown={onCancel}>
      <div className="modal modal-choice" onMouseDown={(e) => e.stopPropagation()}>
        <h2>選択肢を編集</h2>

        <div className="choice-list">
          {choices.map((c, i) => (
            <div key={i} className="choice-row">
              <div className="choice-num">{i + 1}</div>
              <div className="choice-fields">
                <label>
                  表示テキスト
                  <input
                    type="text"
                    value={c.text}
                    onChange={(e) => updateChoice(i, { text: e.target.value })}
                    placeholder="例: うん、よろしく"
                  />
                </label>
                <label>
                  ジャンプ先(#label / path#label)
                  <input
                    type="text"
                    value={c.target}
                    onChange={(e) => updateChoice(i, { target: e.target.value })}
                    placeholder="#greet_back"
                    list={labelSuggestions ? "label-list" : undefined}
                  />
                </label>
                <label>
                  条件(任意 @if)
                  <input
                    type="text"
                    value={c.condition ?? ""}
                    onChange={(e) => updateChoice(i, { condition: e.target.value })}
                    placeholder="例: affection >= 5(空欄なら常時表示)"
                  />
                </label>
              </div>
              <div className="choice-actions">
                <button
                  className="btn btn-tiny btn-secondary"
                  onClick={() => moveUp(i)}
                  disabled={i === 0}
                  title="上へ"
                >
                  ↑
                </button>
                <button
                  className="btn btn-tiny btn-secondary"
                  onClick={() => moveDown(i)}
                  disabled={i === choices.length - 1}
                  title="下へ"
                >
                  ↓
                </button>
                <button
                  className="btn btn-tiny btn-warn"
                  onClick={() => removeChoice(i)}
                  title="削除"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>

        {labelSuggestions && (
          <datalist id="label-list">
            {labelSuggestions.map((l) => (
              <option key={l} value={`#${l}`} />
            ))}
          </datalist>
        )}

        <button className="btn btn-secondary choice-add" onClick={addChoice}>
          + 選択肢を追加
        </button>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onCancel}>
            キャンセル
          </button>
          <button className="btn btn-primary" onClick={save}>
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
